import {
    AvailableSmartAssistantImportDTO,
    ChangingBudgetImportResultDTO,
    ChangingEmployeeGroupImportResultDTO,
    ChangingOfficeImportResultDTO,
    ChangingRegisteredEmployeeGroupImportResultDTO,
    ChangingTimeInLieuImportResultDTO,
    CustomerReportingDataImportResultDTO,
    LeavingEmployeesImportResultDTO,
    NewEmployeesImportResultDTO,
    SmartAssistantDateFormat,
    SmartAssistantImportColumnKey,
    SmartAssistantImportStatus,
    SmartAssistantType,
    SolutionPartnerIdentificationNumberImportResultDTO,
    UrbanSportsClubMembershipStatusImportRequestDTO,
    UrbanSportsClubMembershipStatusImportResultDTO
} from '@iota/inh-core-v1-management-client';

export type SmartAssistantOwner = 'COMPANY' | 'SOLUTION_PARTNER';

export type SmartAssistantValidationResult =
    | SolutionPartnerIdentificationNumberImportResultDTO
    | UrbanSportsClubMembershipStatusImportResultDTO
    | ChangingBudgetImportResultDTO
    | ChangingEmployeeGroupImportResultDTO
    | ChangingRegisteredEmployeeGroupImportResultDTO
    | ChangingOfficeImportResultDTO
    | LeavingEmployeesImportResultDTO
    | NewEmployeesImportResultDTO
    | ChangingTimeInLieuImportResultDTO
    | CustomerReportingDataImportResultDTO;

export interface SmartAssistantTile {
    availableImport: AvailableSmartAssistantImportDTO;
    cardTitle: string;
    cardSubtitle: string;
}

export const SmartAssistantStep = {
    UPLOAD_FILE: 'UPLOAD_FILE',
    ASSIGN_COLUMNS: 'ASSIGN_COLUMNS',
    VALIDATE_DATA: 'VALIDATE_DATA',
    COMPLETION: 'COMPLETION',

    URBAN_SPORTS_CLUB_DATE: 'URBAN_SPORTS_CLUB_DATE',
    PRODUCT: 'PRODUCT'
} as const;
export type SmartAssistantStep = (typeof SmartAssistantStep)[keyof typeof SmartAssistantStep];

export const SMART_ASSISTANT_STEP_LABELS: Record<SmartAssistantStep, string> = {
    [SmartAssistantStep.UPLOAD_FILE]: $localize`Upload Excel file`,
    [SmartAssistantStep.ASSIGN_COLUMNS]: $localize`Assign columns`,
    [SmartAssistantStep.VALIDATE_DATA]: $localize`Validate data`,
    [SmartAssistantStep.COMPLETION]: $localize`Complete`,

    [SmartAssistantStep.URBAN_SPORTS_CLUB_DATE]: $localize`Date`,
    [SmartAssistantStep.PRODUCT]: $localize`Product`
};

export interface SmartAssistantProductOption {
    id: string;
    label: string;
}

export type SmartAssistantProductStepStatus = 'loading' | 'error' | 'empty' | 'ready';

export const SMART_ASSISTANT_DEFAULT_STEPS: SmartAssistantStep[] = [
    SmartAssistantStep.UPLOAD_FILE,
    SmartAssistantStep.ASSIGN_COLUMNS,
    SmartAssistantStep.VALIDATE_DATA,
    SmartAssistantStep.COMPLETION
];

export interface SmartAssistantTypeConfig {
    type: SmartAssistantType;
    steps: SmartAssistantStep[];
    cardTitle: string;
    cardSubtitle: string;
    dialogTitle: string;
    firstStepTitle: string;
    firstStepDescription: string;
    explainedColumns: SmartAssistantImportColumnKey[];
}

export const SMART_ASSISTANT_TYPE_CONFIGS: Partial<Record<SmartAssistantType, SmartAssistantTypeConfig>> = {
    EMPLOYEE_SOLUTION_PARTNER_IDENTIFICATION_NUMBER_IMPORT: {
        type: SmartAssistantType.EMPLOYEE_SOLUTION_PARTNER_IDENTIFICATION_NUMBER_IMPORT,
        steps: [SmartAssistantStep.PRODUCT, ...SMART_ASSISTANT_DEFAULT_STEPS],
        cardTitle: $localize`Import user identification no.`,
        cardSubtitle: $localize`Import the user identification no. provided by the solution partner.`,
        dialogTitle: $localize`Import user identification no.`,
        firstStepTitle: $localize`Import Excel file with employees and the identification no.`,
        firstStepDescription: $localize`This tool allows you to import the user identification no. provided by the solution partner from an Excel file. This can be e.g. a member ID, insurance no. or similar.`,
        explainedColumns: [SmartAssistantImportColumnKey.EMPLOYEE_SOLUTION_PARTNER_IDENTIFICATION_NUMBER]
    },
    URBAN_SPORTS_CLUB_MEMBERSHIP_STATUS_IMPORT: {
        type: SmartAssistantType.URBAN_SPORTS_CLUB_MEMBERSHIP_STATUS_IMPORT,
        steps: [
            SmartAssistantStep.URBAN_SPORTS_CLUB_DATE,
            SmartAssistantStep.UPLOAD_FILE,
            SmartAssistantStep.ASSIGN_COLUMNS,
            SmartAssistantStep.VALIDATE_DATA,
            SmartAssistantStep.COMPLETION
        ],
        cardTitle: $localize`USC membership sync`,
        cardSubtitle: $localize`Synchronize memberships with our user subscription base. Only confirmed memberships will be charged to customers.`,
        dialogTitle: $localize`USC membership sync`,
        firstStepTitle: $localize`Import Excel file and confirm active USC memberships`,
        firstStepDescription: $localize`This tool allows you to confirm the prices of users with an active USC membership.`,
        explainedColumns: [SmartAssistantImportColumnKey.CHAEMP_ID, SmartAssistantImportColumnKey.URBAN_SPORTS_CLUB_MEMBERSHIP_STATUS]
    },
    CHANGING_BUDGET: {
        type: SmartAssistantType.CHANGING_BUDGET,
        steps: SMART_ASSISTANT_DEFAULT_STEPS,
        cardTitle: $localize`Adjust budgets`,
        cardSubtitle: $localize`Adjust the budget of the employees`,
        dialogTitle: $localize`Adjust budget`,
        firstStepTitle: $localize`Import Excel file with employees and budgets`,
        firstStepDescription: $localize`This tool allows you to import and update the budgets of multiple employees from an Excel file.`,
        explainedColumns: [SmartAssistantImportColumnKey.YEARLY_BUDGET]
    },
    CHANGING_EMPLOYEE_GROUP: {
        type: SmartAssistantType.CHANGING_EMPLOYEE_GROUP,
        steps: SMART_ASSISTANT_DEFAULT_STEPS,
        cardTitle: $localize`Customize employee groups (non-registered)`,
        cardSubtitle: $localize`Adjust the groups of non-registered employees`,
        dialogTitle: $localize`Customize employee groups (non-registered)`,
        firstStepTitle: $localize`Import Excel file with non-registered employees and group assignment`,
        firstStepDescription: $localize`This tool allows you to import and update the groups of multiple non-registered employees from an Excel file.`,
        explainedColumns: [SmartAssistantImportColumnKey.EMPLOYEE_GROUP]
    },
    CHANGING_REGISTERED_EMPLOYEE_GROUP: {
        type: SmartAssistantType.CHANGING_REGISTERED_EMPLOYEE_GROUP,
        steps: SMART_ASSISTANT_DEFAULT_STEPS,
        cardTitle: $localize`Customize employee groups (registered)`,
        cardSubtitle: $localize`Adjust the groups of registered employees`,
        dialogTitle: $localize`Customize employee groups (registered)`,
        firstStepTitle: $localize`Import Excel file with registered employees and group assignment`,
        firstStepDescription: $localize`This tool allows you to import and update the groups of multiple registered employees from an Excel file.`,
        explainedColumns: [SmartAssistantImportColumnKey.EMPLOYEE_GROUP, SmartAssistantImportColumnKey.EMPLOYEE_GROUP_CHANGE_DATE]
    },
    CHANGING_OFFICE: {
        type: SmartAssistantType.CHANGING_OFFICE,
        steps: SMART_ASSISTANT_DEFAULT_STEPS,
        cardTitle: $localize`Customize offices`,
        cardSubtitle: $localize`Adjust the offices of the employees`,
        dialogTitle: $localize`Customize offices`,
        firstStepTitle: $localize`Import Excel file with employees and office assignment`,
        firstStepDescription: $localize`This tool allows you to import and update the offices of multiple employees from an Excel file.`,
        explainedColumns: [SmartAssistantImportColumnKey.OFFICE_NAME]
    },
    LEAVING_EMPLOYEES: {
        type: SmartAssistantType.LEAVING_EMPLOYEES,
        steps: SMART_ASSISTANT_DEFAULT_STEPS,
        cardTitle: $localize`Deregister employee`,
        cardSubtitle: $localize`Deregister employees who are leaving`,
        dialogTitle: $localize`Deregister employee`,
        firstStepTitle: $localize`Import Excel file with employees and leaving date`,
        firstStepDescription: $localize`With this tool you can perform the deregistration of multiple employees from one Excel file.`,
        explainedColumns: [SmartAssistantImportColumnKey.LEAVING_DATE]
    },
    NEW_EMPLOYEES: {
        type: SmartAssistantType.NEW_EMPLOYEES,
        steps: SMART_ASSISTANT_DEFAULT_STEPS,
        cardTitle: $localize`Import new employees`,
        cardSubtitle: $localize`Add new employees`,
        dialogTitle: $localize`New employees`,
        firstStepTitle: $localize`Import Excel file with new employees`,
        firstStepDescription: $localize`With this tool you can import new employees from an Excel file.`,
        explainedColumns: [SmartAssistantImportColumnKey.OFFICE_NAME, SmartAssistantImportColumnKey.EMPLOYEE_GROUP]
    },
    CHANGING_TIME_IN_LIEU: {
        type: SmartAssistantType.CHANGING_TIME_IN_LIEU,
        steps: SMART_ASSISTANT_DEFAULT_STEPS,
        cardTitle: $localize`Adjust time in lieu`,
        cardSubtitle: $localize`Adjust the time in lieu of the employees`,
        dialogTitle: $localize`Adjust time in lieu`,
        firstStepTitle: $localize`Import Excel file with time in lieu of the employees`,
        firstStepDescription: $localize`Import Excel file with employee time off times`,
        explainedColumns: [
            SmartAssistantImportColumnKey.WORKING_DAYS_WEEKLY,
            SmartAssistantImportColumnKey.WORKING_HOURS_WEEKLY,
            SmartAssistantImportColumnKey.FULL_TIME_EQUIVALENT
        ]
    },
    CUSTOMER_REPORTING_DATA: {
        type: SmartAssistantType.CUSTOMER_REPORTING_DATA,
        steps: SMART_ASSISTANT_DEFAULT_STEPS,
        cardTitle: $localize`Upload customer specific data`,
        cardSubtitle: $localize`Provide additional customer specific information for reporting purposes.`,
        dialogTitle: $localize`Upload customer specific data`,
        firstStepTitle: $localize`Import Excel file with employees and customer specific data`,
        firstStepDescription: $localize`With this tool you can import customer specific data for employees from an Excel file.`,
        explainedColumns: []
    }
};

export interface SmartAssistantResultPage {
    route: 'employees' | 'customer-reporting-table';
    label: string;
}

const SHOW_EMPLOYEES_RESULT_PAGE: SmartAssistantResultPage = { route: 'employees', label: $localize`Show employees` };

export const SMART_ASSISTANT_RESULT_PAGES: Partial<Record<SmartAssistantType, SmartAssistantResultPage>> = {
    CHANGING_BUDGET: SHOW_EMPLOYEES_RESULT_PAGE,
    CHANGING_EMPLOYEE_GROUP: SHOW_EMPLOYEES_RESULT_PAGE,
    CHANGING_REGISTERED_EMPLOYEE_GROUP: SHOW_EMPLOYEES_RESULT_PAGE,
    CHANGING_OFFICE: SHOW_EMPLOYEES_RESULT_PAGE,
    LEAVING_EMPLOYEES: SHOW_EMPLOYEES_RESULT_PAGE,
    NEW_EMPLOYEES: SHOW_EMPLOYEES_RESULT_PAGE,
    CHANGING_TIME_IN_LIEU: SHOW_EMPLOYEES_RESULT_PAGE,
    CUSTOMER_REPORTING_DATA: { route: 'customer-reporting-table', label: $localize`Show customer reporting data` }
};

export const SMART_ASSISTANT_GLOBAL_COLUMN_ORDER: SmartAssistantImportColumnKey[] = [
    SmartAssistantImportColumnKey.CHAEMP_ID,
    SmartAssistantImportColumnKey.EMPLOYEE_SOLUTION_PARTNER_IDENTIFICATION_NUMBER,
    SmartAssistantImportColumnKey.URBAN_SPORTS_CLUB_MEMBERSHIP_STATUS,
    SmartAssistantImportColumnKey.EMPLOYEE_NUMBER,
    SmartAssistantImportColumnKey.DATE_OF_BIRTH,
    SmartAssistantImportColumnKey.OFFICE_NAME,
    SmartAssistantImportColumnKey.EMPLOYEE_GROUP,
    SmartAssistantImportColumnKey.EMPLOYEE_GROUP_CHANGE_DATE,
    SmartAssistantImportColumnKey.YEARLY_BUDGET,
    SmartAssistantImportColumnKey.LEAVING_DATE,
    SmartAssistantImportColumnKey.REGISTRATION_POSSIBLE_FROM,
    SmartAssistantImportColumnKey.ALTERNATIVE_EMPLOYEE_NUMBER,
    SmartAssistantImportColumnKey.WORKING_DAYS_WEEKLY,
    SmartAssistantImportColumnKey.WORKING_HOURS_WEEKLY,
    SmartAssistantImportColumnKey.FULL_TIME_EQUIVALENT,
    SmartAssistantImportColumnKey.CUSTOM_VARIABLE_1,
    SmartAssistantImportColumnKey.CUSTOM_VARIABLE_2,
    SmartAssistantImportColumnKey.CUSTOM_VARIABLE_3,
    SmartAssistantImportColumnKey.CUSTOM_VARIABLE_4,
    SmartAssistantImportColumnKey.CUSTOM_VARIABLE_5,
    SmartAssistantImportColumnKey.CUSTOM_VARIABLE_6,
    SmartAssistantImportColumnKey.CUSTOM_VARIABLE_7,
    SmartAssistantImportColumnKey.CUSTOM_VARIABLE_8,
    SmartAssistantImportColumnKey.CUSTOM_VARIABLE_9,
    SmartAssistantImportColumnKey.CUSTOM_VARIABLE_10
];

export const SMART_ASSISTANT_COLUMN_KEY_LABELS: Record<SmartAssistantImportColumnKey, string> = {
    CHAEMP_ID: $localize`chæmp-ID`,
    EMPLOYEE_SOLUTION_PARTNER_IDENTIFICATION_NUMBER: $localize`Identification-no.`,
    URBAN_SPORTS_CLUB_MEMBERSHIP_STATUS: $localize`Membership Status`,
    EMPLOYEE_NUMBER: $localize`Employee-no.`,
    YEARLY_BUDGET: $localize`Yearly budget`,
    EMPLOYEE_GROUP: $localize`Employee group`,
    EMPLOYEE_GROUP_CHANGE_DATE: $localize`Group change date`,
    OFFICE_NAME: $localize`Office`,
    LEAVING_DATE: $localize`Leaving date`,
    DATE_OF_BIRTH: $localize`Date of birth`,
    REGISTRATION_POSSIBLE_FROM: $localize`Registration possible from`,
    ALTERNATIVE_EMPLOYEE_NUMBER: $localize`Alternative Employee-no.`,
    WORKING_DAYS_WEEKLY: $localize`Working days per week`,
    WORKING_HOURS_WEEKLY: $localize`Weekly working hours`,
    FULL_TIME_EQUIVALENT: $localize`FTE`,
    CUSTOM_VARIABLE_1: `${$localize`Custom variable`} 1`,
    CUSTOM_VARIABLE_2: `${$localize`Custom variable`} 2`,
    CUSTOM_VARIABLE_3: `${$localize`Custom variable`} 3`,
    CUSTOM_VARIABLE_4: `${$localize`Custom variable`} 4`,
    CUSTOM_VARIABLE_5: `${$localize`Custom variable`} 5`,
    CUSTOM_VARIABLE_6: `${$localize`Custom variable`} 6`,
    CUSTOM_VARIABLE_7: `${$localize`Custom variable`} 7`,
    CUSTOM_VARIABLE_8: `${$localize`Custom variable`} 8`,
    CUSTOM_VARIABLE_9: `${$localize`Custom variable`} 9`,
    CUSTOM_VARIABLE_10: `${$localize`Custom variable`} 10`
};

export const SMART_ASSISTANT_COLUMN_EXPLANATIONS: Partial<Record<SmartAssistantImportColumnKey, string>> = {
    CHAEMP_ID: $localize`ID of the user in chæmp.`,
    EMPLOYEE_SOLUTION_PARTNER_IDENTIFICATION_NUMBER: $localize`e.g. member ID or insurance no.`,
    URBAN_SPORTS_CLUB_MEMBERSHIP_STATUS: $localize`Your column must contain the value "active" for all active Urban Sports Club memberships.`,
    YEARLY_BUDGET: $localize`Annual budget of the employee`,
    EMPLOYEE_GROUP: $localize`Group to which the employee is assigned`,
    EMPLOYEE_GROUP_CHANGE_DATE: $localize`First day of the month from which the new group applies`,
    OFFICE_NAME: $localize`Office of the employee`,
    LEAVING_DATE: $localize`Leaving date of the employee. An empty cell cancels a scheduled leaving.`,
    WORKING_DAYS_WEEKLY: $localize`Working days per week`,
    WORKING_HOURS_WEEKLY: $localize`Weekly working hours`,
    FULL_TIME_EQUIVALENT: $localize`full-time equivalent e.g. 0.5`
};

export const SMART_ASSISTANT_DATE_COLUMNS: SmartAssistantImportColumnKey[] = [
    SmartAssistantImportColumnKey.EMPLOYEE_GROUP_CHANGE_DATE,
    SmartAssistantImportColumnKey.LEAVING_DATE,
    SmartAssistantImportColumnKey.DATE_OF_BIRTH,
    SmartAssistantImportColumnKey.REGISTRATION_POSSIBLE_FROM
];

export const SMART_ASSISTANT_TYPES_WITH_DATE_FORMATS: SmartAssistantType[] = [
    SmartAssistantType.CHANGING_REGISTERED_EMPLOYEE_GROUP,
    SmartAssistantType.LEAVING_EMPLOYEES,
    SmartAssistantType.NEW_EMPLOYEES
];

export const SMART_ASSISTANT_DATE_FORMAT_LABELS: Record<SmartAssistantDateFormat, string> = {
    YEAR_MONTH_DAY_DASH: '2026-12-31',
    DAY_MONTH_YEAR_DOT: '31.12.2026',
    DAY_MONTH_YEAR_SLASH: '31/12/2026',
    MONTH_DAY_YEAR_SLASH: '12/31/2026',
    DAY_MONTH_YEAR_DASH: '31-12-2026',
    MONTH_DAY_YEAR_DASH: '12-31-2026'
};

export interface SmartAssistantColumnRow {
    key: SmartAssistantImportColumnKey;
    label: string;
    isDate: boolean;
    dateFormat: SmartAssistantDateFormat | null;
}

export function sortColumnKeysByGlobalOrder(columns: SmartAssistantImportColumnKey[]): SmartAssistantImportColumnKey[] {
    return [...columns].sort((a, b) => SMART_ASSISTANT_GLOBAL_COLUMN_ORDER.indexOf(a) - SMART_ASSISTANT_GLOBAL_COLUMN_ORDER.indexOf(b));
}

export function isCustomVariableColumn(key: SmartAssistantImportColumnKey | string): boolean {
    return key.startsWith('CUSTOM_VARIABLE_');
}

export function toColumnLabel(key: SmartAssistantImportColumnKey, columnLabels: { [key: string]: string }): string {
    return columnLabels[key] || SMART_ASSISTANT_COLUMN_KEY_LABELS[key];
}

export function toLabeledColumns(
    columns: SmartAssistantImportColumnKey[],
    columnLabels: { [key: string]: string }
): { key: SmartAssistantImportColumnKey; label: string }[] {
    return sortColumnKeysByGlobalOrder(columns).map((key) => ({
        key,
        label: toColumnLabel(key, columnLabels)
    }));
}

type MonthEnum = UrbanSportsClubMembershipStatusImportRequestDTO.MonthEnum;

export const MONTH_ENUM_LABELS: Record<MonthEnum, string> = {
    JANUARY: $localize`January`,
    FEBRUARY: $localize`February`,
    MARCH: $localize`March`,
    APRIL: $localize`April`,
    MAY: $localize`May`,
    JUNE: $localize`June`,
    JULY: $localize`July`,
    AUGUST: $localize`August`,
    SEPTEMBER: $localize`September`,
    OCTOBER: $localize`October`,
    NOVEMBER: $localize`November`,
    DECEMBER: $localize`December`
};

export const SMART_ASSISTANT_STATUS_BADGE_CONFIG: Record<SmartAssistantImportStatus, { label: string; color: string }> = {
    PROCESSED: { label: $localize`Processed`, color: 'primary' },
    PROCESSING_FAILED: { label: $localize`Processing failed`, color: 'error' },
    VALIDATION_FAILED: { label: $localize`Validation failed`, color: 'error' },
    CANCELLED: { label: $localize`Cancelled`, color: 'warning' },
    UNKNOWN: { label: $localize`Unknown`, color: 'warning' },
    VALIDATED: { label: $localize`Validated`, color: 'neutral' }
};
